<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="loading" />
  <div v-else class="ds-page">
    <div class="ds-container">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">من نحن</h1>
          <p class="ds-page-header__subtitle">محتوى صفحة "من نحن" الظاهرة على الموقع العام</p>
        </div>
        <div class="ds-page-header__actions">
          <router-link to="/about/edit" class="ds-btn ds-btn--primary">
            <i class="pi pi-pencil"></i> {{ hasContent ? 'تعديل' : 'إضافة المحتوى' }}
          </router-link>
          <button v-if="hasContent" class="ds-btn ds-btn--danger" @click="remove">
            <i class="pi pi-trash"></i> حذف
          </button>
        </div>
      </div>

      <div v-if="!hasContent" class="empty-state empty-state--card">
        <div class="empty-state__icon"><i class="pi pi-info-circle"></i></div>
        <h3 class="empty-state__title">لا يوجد محتوى بعد</h3>
        <p class="empty-state__desc">لم يتم إضافة محتوى صفحة "من نحن" بعد.</p>
        <router-link to="/about/edit" class="ds-btn ds-btn--primary empty-state__action">
          <i class="pi pi-plus"></i> إضافة المحتوى
        </router-link>
      </div>

      <template v-else>
        <span class="status-badge mb-3" :class="about.is_published ? 'status-badge--accepted' : 'status-badge--pending'">
          {{ about.is_published ? 'منشور على الموقع' : 'مسودة — غير ظاهر للزوار' }}
        </span>
        <img v-if="about.image_link" :src="about.image_link" :alt="about.title" class="about-hero" />
        <h2 v-if="about.title" class="about-title">{{ about.title }}</h2>
        <p v-if="about.subtitle" class="about-subtitle">{{ about.subtitle }}</p>
        <div class="post-content" v-html="about.content"></div>

        <div class="about-vm-grid" v-if="about.vision || about.mission">
          <div v-if="about.vision" class="about-vm-card">
            <h3><i class="pi pi-compass"></i> الرؤية</h3>
            <p>{{ about.vision }}</p>
          </div>
          <div v-if="about.mission" class="about-vm-card">
            <h3><i class="pi pi-flag"></i> الرسالة</h3>
            <p>{{ about.mission }}</p>
          </div>
        </div>

        <div class="about-stats-grid" v-if="about.statistics && about.statistics.length">
          <div v-for="(stat, idx) in about.statistics" :key="idx" class="about-stat">
            <div class="about-stat__value">{{ stat.value }}</div>
            <div class="about-stat__label">{{ stat.label }}</div>
          </div>
        </div>

        <div v-if="about.gallery_links && about.gallery_links.length" class="about-gallery">
          <h3 class="about-gallery__title">معرض الصور</h3>
          <div class="about-gallery__grid">
            <img v-for="link in about.gallery_links" :key="link" :src="link" alt="" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';

export default {
  name: 'AboutIndex',
  components: { Breadcrumb, Loading },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'من نحن', path: '/about' }],
      about: null,
      loading: true,
    };
  },
  computed: {
    hasContent() { return !!(this.about && this.about.content); },
  },
  methods: {
    async fetchAbout() {
      this.loading = true;
      try {
        const { data } = await axios.get('/admin/about-page');
        this.about = data.data;
      } finally {
        this.loading = false;
      }
    },
    async remove() {
      const res = await Swal.fire({
        title: 'حذف محتوى الصفحة', text: 'ستعود الصفحة العامة لحالة "لا يوجد محتوى بعد"، وسيتم نقل المحتوى إلى سلة المحذوفات ويمكن استرجاعه لاحقاً.',
        icon: 'warning', showCancelButton: true, confirmButtonText: 'نعم، احذف',
        cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await axios.delete('/admin/about-page');
        Swal.fire({ icon: 'success', title: 'تم الحذف', timer: 1200, showConfirmButton: false });
        await this.fetchAbout();
      } catch (error) {
        Swal.fire({ icon: 'error', title: error.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      }
    },
  },
  mounted() {
    this.fetchAbout();
  },
};
</script>

<style scoped>
.about-hero { width: 100%; max-height: 360px; object-fit: cover; border-radius: var(--radius-lg); margin-bottom: 1.5rem; }
.about-title { font-size: 1.4rem; font-weight: 800; color: var(--text-primary); margin-bottom: 0.4rem; }
.about-subtitle { font-size: 1rem; color: var(--text-secondary); margin-bottom: 1.25rem; }
.post-content { line-height: 1.9; color: var(--text-primary); font-size: 1rem; }
.post-content :deep(img) { max-width: 100%; border-radius: var(--radius-md); }

.about-vm-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1rem; margin-top: 2rem; }
.about-vm-card { background: var(--surface-card); border: 1px solid var(--surface-border); border-radius: var(--radius-lg); padding: 1.25rem; }
.about-vm-card h3 { display: flex; align-items: center; gap: 0.5rem; font-size: 1rem; font-weight: 800; color: var(--text-primary); margin-bottom: 0.5rem; }
.about-vm-card p { color: var(--text-secondary); line-height: 1.7; margin: 0; }

.about-stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 1rem; margin-top: 2rem; }
.about-stat { text-align: center; background: var(--primary-light); border-radius: var(--radius-lg); padding: 1.25rem 0.75rem; }
.about-stat__value { font-size: 1.5rem; font-weight: 800; color: var(--primary-color); }
.about-stat__label { font-size: 0.82rem; color: var(--text-secondary); margin-top: 0.25rem; }
.about-gallery { margin-top: 2rem; }
.about-gallery__title { font-size: 1.1rem; font-weight: 700; margin-bottom: 0.75rem; }
.about-gallery__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 0.75rem; }
.about-gallery__grid img { width: 100%; aspect-ratio: 1; object-fit: cover; border-radius: var(--radius-md); border: 1px solid var(--surface-border); }
</style>
